import { useEffect, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from '../../../i18n/useTranslation';
import { booksellerAuthConfigsService } from '../../../api/booksellerAuthConfigsService';
import { useBooksellerAuthConfig } from '../../../hooks/useBooksellerAuthConfig';
import { SensitiveField, type SensitiveFieldHandle } from './SensitiveField';
import { AuthenticationType } from '../../../types/manageBooksellers';
import type {
  CreateBooksellerAuthConfigDto,
  UpdateBooksellerAuthConfigDto,
} from '../../../types/booksellerAuthConfig';

interface Props {
  booksellerId: string;
  authType: AuthenticationType;
}

interface DraftFields {
  authority: string;
  clientId: string;
  scopes: string;
  responseType: string;
  callbackPath: string;
  signOutCallbackPath: string;
  metadataUrl: string;
  requirePkce: boolean;
  requireHttpsMetadata: boolean;
  additionalParameters: string;
}

const EMPTY_DRAFT: DraftFields = {
  authority: '',
  clientId: '',
  scopes: 'openid profile email',
  responseType: 'code',
  callbackPath: '/signin-oidc',
  signOutCallbackPath: '',
  metadataUrl: '',
  requirePkce: true,
  requireHttpsMetadata: true,
  additionalParameters: '',
};

export default function OAuthLoginTab({ booksellerId, authType }: Props) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { data, isLoading, error } = useBooksellerAuthConfig(booksellerId);

  const [draft, setDraft] = useState<DraftFields>(EMPTY_DRAFT);
  const [secret, setSecret] = useState<string>('');
  const [confirmedClear, setConfirmedClear] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const secretRef = useRef<SensitiveFieldHandle>(null);

  // Hydrate draft when server data lands; do NOT pull ClientSecret into component state.
  // No else branch: the initial EMPTY_DRAFT covers the not-yet-loaded case, and we do
  // NOT want to clobber user input if `data` flips back to undefined during a query
  // invalidation between create-success and the refetched row landing.
  useEffect(() => {
    if (data) {
      setDraft({
        authority: data.authority ?? '',
        clientId: data.clientId ?? '',
        scopes: data.scopes ?? '',
        responseType: data.responseType ?? 'code',
        callbackPath: data.callbackPath ?? '',
        signOutCallbackPath: data.signOutCallbackPath ?? '',
        metadataUrl: data.metadataUrl ?? '',
        requirePkce: data.requirePkce ?? true,
        requireHttpsMetadata: data.requireHttpsMetadata ?? true,
        additionalParameters: data.additionalParameters ?? '',
      });
      secretRef.current?.reset();
    }
  }, [data]);

  const create = useMutation({
    mutationFn: () => {
      const payload: CreateBooksellerAuthConfigDto = {
        booksellerId,
        authority: draft.authority || undefined,
        clientId: draft.clientId,
        clientSecret: secret || undefined,
        scopes: draft.scopes || undefined,
        responseType: draft.responseType,
        callbackPath: draft.callbackPath,
        signOutCallbackPath: draft.signOutCallbackPath || undefined,
        metadataUrl: draft.metadataUrl || undefined,
        requirePkce: draft.requirePkce,
        requireHttpsMetadata: draft.requireHttpsMetadata,
        additionalParameters: draft.additionalParameters || undefined,
      };
      return booksellerAuthConfigsService.create(payload);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['manage-bookseller-auth-config', booksellerId] });
      setSaveError(null);
      setSaveSuccess(true);
      setSecret('');
    },
    onError: (err: unknown) => {
      setSaveSuccess(false);
      setSaveError(readErrorMessage(err));
    },
  });

  const update = useMutation({
    mutationFn: () => {
      if (!data) throw new Error('No existing config to update');
      const touched = secretRef.current?.hasBeenTouched() ?? false;
      const clientSecretToSend = !touched
        ? undefined
        : (secret === '' && confirmedClear ? '' : (secret || undefined));

      const payload: UpdateBooksellerAuthConfigDto = {
        booksellerId,
        authority: draft.authority || undefined,
        clientId: draft.clientId,
        clientSecret: clientSecretToSend,
        scopes: draft.scopes || undefined,
        responseType: draft.responseType,
        callbackPath: draft.callbackPath,
        signOutCallbackPath: draft.signOutCallbackPath || undefined,
        metadataUrl: draft.metadataUrl || undefined,
        requirePkce: draft.requirePkce,
        requireHttpsMetadata: draft.requireHttpsMetadata,
        additionalParameters: draft.additionalParameters || undefined,
        concurrencyStamp: data.concurrencyStamp,
      };
      return booksellerAuthConfigsService.update(data.id, payload);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['manage-bookseller-auth-config', booksellerId] });
      setSaveError(null);
      setSaveSuccess(true);
      setSecret('');
      setConfirmedClear(false);
      secretRef.current?.reset();
    },
    onError: (err: unknown) => {
      setSaveSuccess(false);
      setSaveError(readErrorMessage(err));
    },
  });

  const supported = authType === AuthenticationType.OIDC || authType === AuthenticationType.OAuth2;
  if (!supported) {
    return (
      <p className="text-xs text-neutral-500">
        {t('App.Admin.Booksellers.OAuthLoginTab.NotRequired')}
      </p>
    );
  }

  if (isLoading) return <p className="text-xs text-neutral-500">{t('App.Admin.Booksellers.OAuthLoginTab.Loading')}</p>;
  if (error) return <p className="text-xs text-red-600">{t('App.Admin.Booksellers.OAuthLoginTab.LoadError', { error: String((error as Error).message) })}</p>;

  const hasStoredSecret = !!(data?.clientSecret);
  const canSave = (draft.clientId ?? '').trim().length > 0
    && (draft.responseType ?? '').trim().length > 0
    && (draft.callbackPath ?? '').trim().length > 0
    && (!!data || secret.length > 0);

  return (
    <div className="space-y-3">
      {saveError && <div role="alert" className="bg-red-50 border border-red-200 text-red-700 text-sm px-3 py-2 rounded">{saveError}</div>}
      {saveSuccess && <div role="status" className="bg-green-50 border border-green-200 text-green-700 text-sm px-3 py-2 rounded">{t('App.Admin.Booksellers.OAuthLoginTab.Saved')}</div>}

      <Field label={t('App.Admin.Booksellers.OAuthLoginTab.AuthorityLabel')}><input className={textInput} value={draft.authority} onChange={(e) => setDraft({ ...draft, authority: e.target.value })} /></Field>
      <Field label={t('App.Admin.Booksellers.OAuthLoginTab.ClientIdLabel')}><input className={textInput} value={draft.clientId} onChange={(e) => setDraft({ ...draft, clientId: e.target.value })} /></Field>

      <SensitiveField
        ref={secretRef}
        name="clientSecret"
        label={t('App.Admin.Booksellers.OAuthLoginTab.ClientSecretLabel')}
        required={!data}
        hasStoredValue={hasStoredSecret}
        onChange={setSecret}
        onConfirmClear={() => setConfirmedClear(true)}
      />

      <Field label={t('App.Admin.Booksellers.OAuthLoginTab.ScopesLabel')}><input className={textInput} value={draft.scopes} onChange={(e) => setDraft({ ...draft, scopes: e.target.value })} placeholder="openid profile email" /></Field>
      <Field label={t('App.Admin.Booksellers.OAuthLoginTab.ResponseTypeLabel')}><input className={textInput} value={draft.responseType} onChange={(e) => setDraft({ ...draft, responseType: e.target.value })} /></Field>
      <Field label={t('App.Admin.Booksellers.OAuthLoginTab.CallbackPathLabel')}><input className={textInput} value={draft.callbackPath} onChange={(e) => setDraft({ ...draft, callbackPath: e.target.value })} /></Field>
      <Field label={t('App.Admin.Booksellers.OAuthLoginTab.SignOutCallbackPathLabel')}><input className={textInput} value={draft.signOutCallbackPath} onChange={(e) => setDraft({ ...draft, signOutCallbackPath: e.target.value })} /></Field>
      <Field label={t('App.Admin.Booksellers.OAuthLoginTab.MetadataUrlLabel')}><input className={textInput} type="url" value={draft.metadataUrl} onChange={(e) => setDraft({ ...draft, metadataUrl: e.target.value })} /></Field>

      <div className="flex gap-4">
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={draft.requirePkce} onChange={(e) => setDraft({ ...draft, requirePkce: e.target.checked })} /> {t('App.Admin.Booksellers.OAuthLoginTab.RequirePkce')}</label>
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={draft.requireHttpsMetadata} onChange={(e) => setDraft({ ...draft, requireHttpsMetadata: e.target.checked })} /> {t('App.Admin.Booksellers.OAuthLoginTab.RequireHttpsMetadata')}</label>
      </div>

      <Field label={t('App.Admin.Booksellers.OAuthLoginTab.AdditionalParametersLabel')}>
        <textarea className={`${textInput} font-mono text-xs`} rows={5} value={draft.additionalParameters} onChange={(e) => setDraft({ ...draft, additionalParameters: e.target.value })} />
      </Field>

      <div className="flex justify-end pt-2">
        <button
          type="button"
          onClick={() => (data ? update.mutate() : create.mutate())}
          disabled={!canSave || create.isPending || update.isPending}
          className="px-3 py-1 bg-primary-600 text-white rounded hover:bg-primary-700 disabled:opacity-50"
        >
          {data ? t('App.Admin.Booksellers.OAuthLoginTab.SaveOAuthConfig') : t('App.Admin.Booksellers.OAuthLoginTab.CreateOAuthConfig')}
        </button>
      </div>
    </div>
  );
}

const textInput = 'border border-neutral-300 rounded px-2 py-1 text-sm w-full';

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="text-xs text-neutral-600">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}

function readErrorMessage(err: unknown): string {
  const e = err as { response?: { data?: { error?: { message?: string } } }; message?: string };
  return e?.response?.data?.error?.message ?? e?.message ?? 'Save failed';
}
